import React, { useEffect } from 'react';
import './DataSourcesModal.css';

/**
 * DataSourcesModal
 * A bottom-sheet / centered-dialog (≥600px) explaining how Yesod sources community data.
 * Props:
 *   isOpen  {boolean}  — whether to show the modal
 *   onClose {function} — called when the user closes it
 */
const DataSourcesModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="dsm-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label="About Data Sources"
    >
      <div className="dsm-modal">
        <div className="dsm-modal__head">
          <span className="dsm-modal__title">About Data Sources</span>
          <button className="dsm-modal__close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div className="dsm-modal__body">
          <h4>How Yesod Sources and Uses Information in Our App</h4>
          <p>
            To provide users with comprehensive and up-to-date information about Jewish
            communities, events, and practices, Yesod displays content sourced from
            publicly available materials and other lawful sources. This may include
            community pages, event listings, schedules, and related informational content
            within the app.
          </p>
          <p>
            Yesod is an independent platform and is not affiliated with, associated with,
            endorsed by, or sponsored by any synagogue, rabbi, organization, or community
            unless explicitly stated. The inclusion of a community or event in the app
            does not imply partnership, approval, or endorsement.
          </p>

          <h4>Sources of Information</h4>
          <p>Information displayed on Yesod may be compiled from one or more of the following:</p>
          <ul>
            <li>
              Publicly available information, including content from community websites,
              publicly shared calendars, event pages, and public social media posts.
            </li>
            <li>Licensed or authorized third-party data providers, where applicable.</li>
            <li>
              User contributions, including information, photos, corrections, or other
              content submitted by users or community representatives.
            </li>
          </ul>
          <p>
            Yesod may collect, organize, standardize, and format publicly available data
            for display within the app. While we strive for accuracy, we do not guarantee
            that publicly sourced information is complete, accurate, or current.
          </p>

          <h4>AI-Generated Content</h4>
          <p>
            Yesod may generate AI-based summaries or informational overviews using one or
            more sources (for example, summaries of community descriptions, event
            highlights, or specific topics). These summaries are automatically generated
            for convenience and informational purposes only and may not fully reflect the
            official position or most current updates of any organization.
          </p>

          <h4>Corrections and Removal Requests</h4>
          <p>If you believe information displayed on Yesod is inaccurate, outdated, or should be removed, you may:</p>
          <ul>
            <li>Suggest an edit within the app (where available), or</li>
            <li>
              Submit a request to{' '}
              <a href="mailto:shalom@yesod.us">shalom@yesod.us</a>
            </li>
          </ul>
          <p>
            For legal removal requests (including intellectual property or other legal
            claims), please contact{' '}
            <a href="mailto:shalom@yesod.us">shalom@yesod.us</a>{' '}
            with sufficient detail to evaluate the request.
          </p>
          <p>
            For more information about how Yesod collects, processes, and protects data,
            please review our{' '}
            <a href="/privacy-policy">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DataSourcesModal;
